/* eslint-disable no-prototype-builtins */
import { DI } from 'aurelia';
import { IHttpClient } from '@aurelia/fetch-client';
import { IConfigService } from './config-service';
import { ResponseResults } from '../resources/models/response-results';
import { GrantType, ErrorMessages } from '../resources/constants';

const TOKEN_ENDPOINT = '/token';
const AUTH_TOKEN_KEY = '__iaanyware_auth_token';
const FORM_TYPE = 'application/x-www-form-urlencoded';
const REQUEST_TYPE_POST = 'POST';
const CLIENT_ID = 'scheme-js';
const CLIENT_SECRET = '2dc34b863374412491f64cfb427c6daa';
const CLIENT_USER_TYPE = 'client';

export const ITokenService = DI.createInterface<ITokenService>(
    'ITokenService',
    (x) => x.singleton(TokenService)
);
export interface ITokenService extends TokenService {}

export class TokenService {
    private errorMessages = ErrorMessages;
    private clientApiHost;
    private appClientType;
    private appClientInstance;

    constructor(
        @IHttpClient readonly http: IHttpClient,
        @IConfigService readonly configService: IConfigService
    ) {
        this.clientApiHost = this.configService.get('clientApiHost');
        this.appClientType = this.configService.get('appClientType');
        this.appClientInstance = this.configService.get('appClientInstance');
    }

    async createAuthToken(username = null, password = null) {
        const responseModel = new ResponseResults();
        let body = {
            grant_type: GrantType.credentials,
            client_secret: CLIENT_SECRET,
            app_client_type: this.appClientType,
            app_client_instance: this.appClientInstance,
            client_id: CLIENT_ID,
        };

        if (username !== null && password !== null) {
            body = {
                grant_type: GrantType.password,
                username: username,
                password: password,
                user_type: CLIENT_USER_TYPE,
                app_client_type: this.appClientType,
                app_client_instance: this.appClientInstance,
                client_id: CLIENT_ID,
            } as any;
        }

        try {
            const tokenResult = await this.http.fetch(
                `${this.clientApiHost}${TOKEN_ENDPOINT}`,
                {
                    method: REQUEST_TYPE_POST,
                    headers: {
                        'content-type': FORM_TYPE,
                    },
                    body: $.param(body),
                }
            );

            responseModel.setStatus(tokenResult.status);
            responseModel.setResults(await tokenResult.json());
            this.saveToSessionStorage(responseModel.getResults());
        } catch (error) {
            responseModel.setStatus(error.status);

            if (responseModel.getStatus() === 400) {
                const serverError = await error.json();

                if (serverError.hasOwnProperty('EmailNotConfirmed')) {
                    responseModel.setErrors({
                        code: 'EmailNotConfirmed',
                        message: serverError.EmailNotConfirmed[0],
                    });
                }

                if (serverError.hasOwnProperty('invalid_password')) {
                    responseModel.setErrors({
                        code: 'LoginError',
                        message: this.errorMessages.LoginError,
                    });
                }

                if (serverError.hasOwnProperty('UserNotFound')) {
                    responseModel.setErrors({
                        code: 'UserNotFound',
                        message: this.errorMessages.UserNotFound,
                    });
                }

                if (serverError.hasOwnProperty('BranchInvalidForScheme')) {
                    responseModel.setErrors({
                        code: 'BranchInvalidForScheme',
                        message: this.errorMessages.BranchInvalidForScheme,
                    });
                }

                if (serverError.hasOwnProperty('NoSchemeGuidSet')) {
                    responseModel.setErrors({
                        code: 'NoSchemeGuidSet',
                        message: this.errorMessages.NoSchemeGuidSet,
                    });
                }
            }

            if (responseModel.getStatus() === 500) {
                responseModel.setErrors({
                    code: 'UnspecifiedError',
                    message: this.errorMessages.UnspecifiedError,
                });
            }
        }

        return responseModel;
    }

    async refreshAuthToken() {
        const responseModel = new ResponseResults();

        const tokenResult = await this.http.fetch(
            `${this.clientApiHost}${TOKEN_ENDPOINT}`,
            {
                method: REQUEST_TYPE_POST,
                headers: {
                    'content-type': FORM_TYPE,
                },
                body: $.param({
                    grant_type: GrantType.refresh,
                    refresh_token: sessionStorage.getItem('refresh_token'),
                    client_id: CLIENT_ID,
                }),
            }
        );

        responseModel.setStatus(tokenResult.status);
        responseModel.setResults(await tokenResult.json());
        this.saveToSessionStorage(responseModel.getResults());

        return responseModel;
    }

    getAuthToken() {
        return sessionStorage.getItem('access_token');
    }

    saveToSessionStorage(token) {
        sessionStorage.setItem('access_token', token.access_token);
        sessionStorage.setItem('refresh_token', token.refresh_token);
        sessionStorage.setItem('access_token_expiry', token['.expires']);
    }
}
